import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  MessageSquare, Settings, Hash, CheckCircle, ArrowRight,
  Bell, Shield, Zap, Send, Clock
} from 'lucide-react';

export default function DocsSlackIntegration() {
  return (
    <div className="max-w-4xl">
      {/* Hero */}
      <div className="mb-12">
        <Badge className="bg-[#4A154B]/40 text-[#E01E5A] border-[#E01E5A]/30 mb-4">
          Slack Integration
        </Badge>
        <h1 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Slack Integration
        </h1>
        <p className="text-lg text-[#94A3B8] mb-6">
          Route exception approvals into Slack and build a Decision Graph of what you chose — and why.
          Approve/reject exceptions in channel without leaving your workflow.
        </p>
        <div className="flex gap-4">
          <Link to="/dashboard/settings/integrations/slack">
            <Button className="bg-[#4A154B] text-white hover:bg-[#4A154B]/80" data-testid="go-to-slack-settings">
              Open Slack Settings <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>

      {/* Why Slack */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          Why Slack Integration?
        </h2>
        <div className="bg-[#0D1628] border border-[#22304A] rounded-lg p-6">
          <p className="text-[#94A3B8] mb-4">
            Commerce Index turns Slack moments into durable assets:
          </p>
          <ul className="space-y-3">
            <li className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
              <div>
                <span className="text-white font-medium">Approval workflows</span>
                <p className="text-sm text-[#94A3B8]">Approve or reject directly from Slack buttons</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
              <div>
                <span className="text-white font-medium">Decision traces</span>
                <p className="text-sm text-[#94A3B8]">Every decision is captured with who, when, and why</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
              <div>
                <span className="text-white font-medium">Precedent you can reuse</span>
                <p className="text-sm text-[#94A3B8]">Turn resolved exceptions into searchable precedent</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
              <div>
                <span className="text-white font-medium">A system you can audit later</span>
                <p className="text-sm text-[#94A3B8]">Full history of decisions for compliance and review</p>
              </div>
            </li>
          </ul>
          <p className="text-[#94A3B8] mt-4">
            <strong className="text-white">Result:</strong> Fewer repeats, faster decisions, and tighter operational consistency.
          </p>
        </div>
      </section>

      {/* Setup Steps */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          Setup Steps
        </h2>
        <div className="space-y-4">
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-sky-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-sky-400 font-bold">1</span>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-2">Create a Slack App</h3>
                  <p className="text-[#94A3B8] text-sm mb-3">
                    Go to <a href="https://api.slack.com/apps" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">api.slack.com/apps</a> and create a new app for your workspace.
                  </p>
                  <div className="p-3 bg-[#1E293B] rounded-lg">
                    <p className="text-xs text-[#64748B]">
                      <strong>Required Scopes:</strong> chat:write, commands, users:read
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-amber-400 font-bold">2</span>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-2">Get Your Bot Token & Signing Secret</h3>
                  <p className="text-[#94A3B8] text-sm mb-3">
                    Copy the Bot User OAuth Token and Signing Secret from your Slack app settings.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-emerald-400 font-bold">3</span>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-2">Register in Commerce Index</h3>
                  <p className="text-[#94A3B8] text-sm mb-3">
                    Go to <Link to="/dashboard/settings/integrations/slack" className="text-emerald-400 hover:underline">Slack Settings</Link> and enter your credentials to connect.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-purple-400 font-bold">4</span>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-2">Set Default Channel</h3>
                  <p className="text-[#94A3B8] text-sm mb-3">
                    Choose a default channel where approval requests will be posted.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-rose-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-rose-400 font-bold">5</span>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-2">Test the Connection</h3>
                  <p className="text-[#94A3B8] text-sm mb-3">
                    Send a test message to verify everything is working correctly.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Features */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          Slack Features
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader className="pb-2">
              <Send className="w-6 h-6 text-sky-400 mb-2" />
              <CardTitle className="text-white">Approval Requests</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-[#94A3B8]">
                Send exception approvals to Slack with approve/reject buttons. 
                Decisions are captured automatically.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader className="pb-2">
              <Bell className="w-6 h-6 text-amber-400 mb-2" />
              <CardTitle className="text-white">Auto-Reminders</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-[#94A3B8]">
                Digest-style reminders for pending approvals. 
                Configurable schedule to avoid channel spam.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader className="pb-2">
              <MessageSquare className="w-6 h-6 text-emerald-400 mb-2" />
              <CardTitle className="text-white">Weekly Digest</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-[#94A3B8]">
                Auto-send your Promise Health snapshot to Slack weekly. 
                Great for ops reviews.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader className="pb-2">
              <Clock className="w-6 h-6 text-purple-400 mb-2" />
              <CardTitle className="text-white">Delivery Log</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-[#94A3B8]">
                View all Slack digest deliveries with status, timestamps, 
                and error details for troubleshooting.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Slack Message Format */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          Approval Message Format
        </h2>
        <div className="bg-[#1E293B] border border-[#334155] rounded-lg p-6">
          <div className="border-l-4 border-amber-400 pl-4">
            <p className="text-white font-semibold mb-2">🔔 Approval Request</p>
            <p className="text-[#94A3B8] text-sm mb-3">
              <strong>Type:</strong> Cancellation Override<br />
              <strong>Order:</strong> #12345<br />
              <strong>Severity:</strong> High<br />
              <strong>Rationale:</strong> Customer requested same-day cancellation after cutoff...
            </p>
            <div className="flex gap-2 mt-4">
              <span className="inline-flex items-center px-3 py-1 rounded bg-emerald-500/20 text-emerald-400 text-sm">
                ✓ Approve
              </span>
              <span className="inline-flex items-center px-3 py-1 rounded bg-rose-500/20 text-rose-400 text-sm">
                ✗ Reject
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Plan Availability */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          Plan Availability
        </h2>
        <div className="space-y-4">
          <Card className="bg-gradient-to-r from-sky-900/30 to-sky-800/20 border-sky-500/30">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-3">
                <Badge className="bg-sky-500/20 text-sky-400 border-sky-500/30">Starter</Badge>
                <span className="text-white font-semibold">Slack Digest (one-way posting)</span>
              </div>
              <p className="text-[#94A3B8] text-sm">
                Receive weekly Promise Health snapshots in Slack.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-emerald-900/30 to-emerald-800/20 border-emerald-500/30">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-3">
                <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">Pro</Badge>
                <span className="text-white font-semibold">Full Slack Approvals + Workflows</span>
              </div>
              <p className="text-[#94A3B8] text-sm">
                Approve/reject in channel, approval queue, snooze, auto-reminders, and precedent.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQ */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          Slack FAQ
        </h2>
        <div className="space-y-4">
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="p-4">
              <h3 className="text-white font-medium mb-2">Do I need Slack for Commerce Index to work?</h3>
              <p className="text-sm text-[#94A3B8]">
                No. Promise Health works without Slack. Slack becomes valuable when you want approvals 
                and decision workflows embedded into day-to-day operations.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="p-4">
              <h3 className="text-white font-medium mb-2">Can I use multiple Slack channels?</h3>
              <p className="text-sm text-[#94A3B8]">
                Currently, you set one default channel per brand. Support for multiple channels 
                and channel routing is on the roadmap.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="p-4">
              <h3 className="text-white font-medium mb-2">What happens if my Slack token expires?</h3>
              <p className="text-sm text-[#94A3B8]">
                You'll see delivery failures in the Digest Delivery Log. Re-register your workspace 
                with a fresh token to restore connectivity.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Next Steps */}
      <section>
        <h2 className="text-2xl font-semibold text-white mb-6">
          Next Steps
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <Link to="/docs/approval-queue">
            <Card className="bg-[#0D1628] border-[#22304A] hover:border-[#4A154B]/50 transition-colors cursor-pointer h-full">
              <CardContent className="p-4">
                <h3 className="text-white font-medium mb-2">Approval Queue →</h3>
                <p className="text-sm text-[#94A3B8]">Manage pending approvals in bulk</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/docs/exception-radar">
            <Card className="bg-[#0D1628] border-[#22304A] hover:border-[#4A154B]/50 transition-colors cursor-pointer h-full">
              <CardContent className="p-4">
                <h3 className="text-white font-medium mb-2">Exception Radar →</h3>
                <p className="text-sm text-[#94A3B8]">Log and route exceptions</p>
              </CardContent>
            </Card>
          </Link>
        </div>
      </section>
    </div>
  );
}
